import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { newBlockId, type CmsPage, type CmsBlock, type CmsStatus } from "@/lib/admin/cms/store";
import { adminCmsPageQuery } from "@/lib/cms/queries";
import { saveCmsPageFn, setCmsPageStatusFn } from "@/lib/cms/cms.functions";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { MediaUrlField } from "@/components/admin/MediaPicker";
import { CmsPageBuilder } from "@/components/admin/CmsPageBuilder";
import { sectionsFor } from "@/lib/cms/schema";
import { logAction } from "@/lib/admin/audit/log";
import { notify } from "@/lib/admin/notify";

export const Route = createFileRoute("/admin/cms/$slug")({ component: CmsEditor });

function CmsEditor() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();
  const { data: page, isLoading } = useQuery(adminCmsPageQuery(slug));
  const [draft, setDraft] = useState<CmsPage | undefined>(page ?? undefined);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  // Adopt server state only when it is safe: a different page, the first
  // load, or after our own save (which clears `dirty`). Never clobber
  // unsaved edits.
  useEffect(() => {
    if (!page) return;
    setDraft((d) => (d && d.slug === page.slug && dirtyRef.current ? d : page));
  }, [page?.slug, page?.updatedAt]);

  // Guard against accidental loss of edited content.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const edit = (next: CmsPage | undefined) => { setDirty(true); setDraft(next); };
  const editFn = (fn: (d: CmsPage) => CmsPage) => { setDirty(true); setDraft((d) => (d ? fn(d) : d)); };

  if (isLoading && !draft) {
    return <div className="flex flex-col gap-3"><PageHeader title="Loading page…"/></div>;
  }
  if (!page || !draft) {
    return <div className="flex flex-col gap-3"><PageHeader title="Page not found"/><Link to="/admin/cms" className="text-sm text-[#C6A15B] hover:underline">Back to CMS</Link></div>;
  }

  /** Refresh admin + storefront caches so published content updates at once. */
  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ["cms"] });
  };

  /**
   * Persist the editor content. `status` is only sent when the action is
   * meant to change publication state, so plain Save never publishes and
   * never turns a live page into a draft.
   */
  const persist = async (status: CmsStatus | undefined, label: string, description: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const saved = (await saveCmsPageFn({
        data: {
          slug,
          title: draft.title,
          description: draft.description,
          seo: draft.seo,
          blocks: draft.blocks,
          ...(status ? { status } : {}),
        },
      })) as CmsPage;
      setDirty(false);
      setDraft(saved);
      qc.setQueryData(adminCmsPageQuery(slug).queryKey, saved);
      await invalidate();
      logAction({ entity: "content", entityId: slug, action: status === "published" ? "publish" : "update", summary: `${label} ${saved.title}` });
      notify.success(label, description);
    } catch (err) {
      notify.error("Could not save", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const save = () => void persist(undefined, "Changes saved", page.status === "published" ? "Live page updated." : "Saved without publishing.");
  const saveDraft = () => void persist("draft", "Draft saved", "This page is not visible on the storefront.");
  const publish = () => void persist("published", "Page published", "The latest content is now live.");

  const unpublish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = (await setCmsPageStatusFn({ data: { slug, status: "draft" } })) as CmsPage;
      setDirty(false);
      setDraft(next);
      qc.setQueryData(adminCmsPageQuery(slug).queryKey, next);
      await invalidate();
      logAction({ entity: "content", entityId: slug, action: "unpublish", summary: `Unpublished ${next.title}` });
      notify.success("Page unpublished");
    } catch (err) {
      notify.error("Could not unpublish", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const setBlock = (id: string, patch: Partial<CmsBlock>) => editFn((d) => ({ ...d, blocks: d.blocks.map((b) => b.id === id ? { ...b, ...patch } as CmsBlock : b) }));
  const removeBlock = (id: string) => editFn((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== id) }));
  const addBlock = (kind: CmsBlock["kind"]) => {
    const base = { id: newBlockId() };
    let next: CmsBlock;
    if (kind === "hero") next = { ...base, kind: "hero", title: "New hero", subtitle: "", imageAlt: "" };
    else if (kind === "richtext") next = { ...base, kind: "richtext", heading: "Heading", body: "Body text..." };
    else if (kind === "image") next = { ...base, kind: "image", imageUrl: "", imageAlt: "" };
    else if (kind === "gallery") next = { ...base, kind: "gallery", items: [] };
    else if (kind === "video") next = { ...base, kind: "video", provider: "youtube", url: "" };
    else if (kind === "cta") next = { ...base, kind: "cta", heading: "Call to action", primaryCta: { label: "Shop", href: "/shop" } };
    else next = { ...base, kind: "faq", heading: "FAQ", items: [] };
    editFn((d) => ({ ...d, blocks: [...d.blocks, next] }));
  };

  // Pages that mirror a hand-built storefront page get the structured Page
  // Builder instead of the generic block list.
  const structured = sectionsFor(slug) !== null;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={`Edit: ${page.title}`} subtitle={`/${slug}`} action={
        <div className="flex items-center gap-2">
          {page.status === "published" ? <StatusPill tone="success">Published</StatusPill> : <StatusPill tone="warn">Draft</StatusPill>}
          {dirty && <span className="text-[10px] font-semibold uppercase tracking-widest text-black/40">Unsaved</span>}
          <button onClick={save} disabled={busy} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
          <button onClick={saveDraft} disabled={busy} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest disabled:opacity-50">Save draft</button>
          {page.status === "published"
            ? <button onClick={() => void unpublish()} disabled={busy} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest disabled:opacity-50">Unpublish</button>
            : <button onClick={publish} disabled={busy} className="border border-black bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white disabled:opacity-50">Publish</button>}
        </div>
      }/>

      <div className={structured ? "grid gap-4" : "grid gap-4 lg:grid-cols-[1fr_320px]"}>
        <div className="flex flex-col gap-4">
          <div className="border border-black/10 bg-white p-4">
            <h3 className="text-sm font-semibold text-black">Meta</h3>
            <div className="mt-3 grid gap-2 text-sm">
              <TextField label="Title" value={draft.title} onChange={(v) => edit({ ...draft, title: v })}/>
              <TextField label="Description" value={draft.description} onChange={(v) => edit({ ...draft, description: v })}/>
              <TextField label="SEO Title" value={draft.seo.title ?? ""} onChange={(v) => edit({ ...draft, seo: { ...draft.seo, title: v } })}/>
              <TextArea label="SEO Description" value={draft.seo.description ?? ""} onChange={(v) => edit({ ...draft, seo: { ...draft.seo, description: v } })}/>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {structured ? (
              <CmsPageBuilder
                slug={slug}
                blocks={draft.blocks}
                onChange={(next) => editFn((d) => ({ ...d, blocks: next }))}
              />
            ) : draft.blocks.map((b, i) => (
              <div key={b.id} className="border border-black/10 bg-white p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-[#C6A15B]">Block {i + 1} - {b.kind}</span>
                  <button onClick={() => removeBlock(b.id)} className="text-[11px] uppercase tracking-widest text-red-700 hover:underline">Remove</button>
                </div>
                <BlockEditor block={b} onChange={(patch) => setBlock(b.id, patch)}/>
              </div>
            ))}
            {!structured && draft.blocks.length === 0 && <div className="border border-dashed border-black/15 p-6 text-center text-sm text-black/50">No blocks yet.</div>}
          </div>
        </div>

        {!structured && <aside className="border border-black/10 bg-white p-4">
          <h3 className="text-sm font-semibold text-black">Add block</h3>
          <div className="mt-3 grid gap-2">
            {(["hero", "richtext", "image", "gallery", "video", "cta", "faq"] as const).map((k) => (
              <button key={k} onClick={() => addBlock(k)} className="border border-black/15 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-widest hover:border-black/50">+ {k}</button>
            ))}
          </div>
          <div className="mt-6">
            <Link to="/admin/cms" className="text-[11px] font-semibold uppercase tracking-widest text-black/60 hover:text-black">Back to pages</Link>
          </div>
        </aside>}
      </div>
    </div>
  );
}

function BlockEditor({ block, onChange }: { block: CmsBlock; onChange: (patch: Partial<CmsBlock>) => void }) {
  if (block.kind === "hero") return (
    <div className="grid gap-2 text-sm">
      <TextField label="Eyebrow" value={block.eyebrow ?? ""} onChange={(v) => onChange({ eyebrow: v } as Partial<CmsBlock>)}/>
      <TextField label="Title" value={block.title ?? ""} onChange={(v) => onChange({ title: v } as Partial<CmsBlock>)}/>
      <TextArea label="Subtitle" value={block.subtitle ?? ""} onChange={(v) => onChange({ subtitle: v } as Partial<CmsBlock>)}/>
      <MediaUrlField label="Image URL" value={block.imageUrl ?? ""} onChange={(v) => onChange({ imageUrl: v } as Partial<CmsBlock>)}/>
      <TextField label="Image Alt" value={block.imageAlt ?? ""} onChange={(v) => onChange({ imageAlt: v } as Partial<CmsBlock>)}/>
    </div>
  );
  if (block.kind === "richtext") return (
    <div className="grid gap-2 text-sm">
      <TextField label="Heading" value={block.heading ?? ""} onChange={(v) => onChange({ heading: v } as Partial<CmsBlock>)}/>
      <TextArea label="Body" value={block.body ?? ""} onChange={(v) => onChange({ body: v } as Partial<CmsBlock>)} rows={6}/>
      <p className="text-[11px] text-black/50">Type {"{support_email}"} to show the Support Email from Settings → Contact.</p>
    </div>
  );
  if (block.kind === "image") return (
    <div className="grid gap-2 text-sm">
      <MediaUrlField label="Image URL" value={block.imageUrl ?? ""} onChange={(v) => onChange({ imageUrl: v } as Partial<CmsBlock>)}/>
      <TextField label="Image Alt" value={block.imageAlt ?? ""} onChange={(v) => onChange({ imageAlt: v } as Partial<CmsBlock>)}/>
      <TextField label="Caption" value={block.caption ?? ""} onChange={(v) => onChange({ caption: v } as Partial<CmsBlock>)}/>
    </div>
  );
  if (block.kind === "video") return (
    <div className="grid gap-2 text-sm">
      <TextField label="Video URL" value={block.url ?? ""} onChange={(v) => onChange({ url: v } as Partial<CmsBlock>)}/>
      <MediaUrlField label="Poster" value={block.posterUrl ?? ""} onChange={(v) => onChange({ posterUrl: v } as Partial<CmsBlock>)}/>
      <TextField label="Caption" value={block.caption ?? ""} onChange={(v) => onChange({ caption: v } as Partial<CmsBlock>)}/>
    </div>
  );
  if (block.kind === "cta") return (
    <div className="grid gap-2 text-sm">
      <TextField label="Heading" value={block.heading ?? ""} onChange={(v) => onChange({ heading: v } as Partial<CmsBlock>)}/>
      <TextField label="Body" value={block.body ?? ""} onChange={(v) => onChange({ body: v } as Partial<CmsBlock>)}/>
      <TextField label="Button label" value={block.primaryCta?.label ?? ""} onChange={(v) => onChange({ primaryCta: { ...(block.primaryCta ?? { href: "" }), label: v } } as Partial<CmsBlock>)}/>
      <TextField label="Button href" value={block.primaryCta?.href ?? ""} onChange={(v) => onChange({ primaryCta: { ...(block.primaryCta ?? { label: "" }), href: v } } as Partial<CmsBlock>)}/>
    </div>
  );
  return <div className="text-xs text-black/50">Editor for {block.kind} coming soon.</div>;
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-2 py-2"/>
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; rows?: number }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">{label}</span>
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-2 py-2"/>
    </label>
  );
}